import React, { useState, useEffect } from "react";
import axios from "axios";
import SearchBar from "./SearchBar";
import BidDetailModal from "./BidDetailModal";
import "../../css/global.css";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { Loader } from "react-feather";
import {InformationCircleIcon} from "@heroicons/react/16/solid";
import {BadgeInfoIcon} from "lucide-react";

const today = dayjs().format("YYYY-MM-DD");
const minDate = dayjs().subtract(60, "day").format("YYYY-MM-DD");

const formatDate = (datetime) => {
    if (!datetime) return "-";
    return dayjs(datetime).format("YYYY-MM-DD HH:mm");
};

const MainSearchPage = () => {
    const [showNotice, setShowNotice] = useState(false);
    const [keywords, setKeywords] = useState([]);

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [highlighted, setHighlighted] = useState("");
    const [newKeyword, setNewKeyword] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [sortKey, setSortKey] = useState("postDate");
    const [sortAsc, setSortAsc] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

// 키워드 목록을 콤마로 합쳐서 서버에 저장
    const saveKeywordsToServer = async (keywordList) => {
        try {
            await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/employees/me/keywords`,
                { keywords: keywordList.join(",") }, // keywordList 사용!
            );
        } catch (e) {
            // alert("키워드 저장에 실패했습니다.");
        }
    };

// 마운트시 서버에서 키워드 불러오기
    const fetchUserKeyword = async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/employees/me/keywords`
            );
            setKeywords(res.data); // List<String>
        } catch (err) {
            // alert("내 정보를 불러오지 못했습니다.");
        }
    };

    useEffect(() => {
        fetchUserKeyword();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        setResults([]);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/bids/list`, {
                params: {
                    keywords,
                    startDate,
                    endDate,
                },
                paramsSerializer: (params) => {
                    const query = new URLSearchParams();
                    params.keywords.forEach((k) => query.append("keywords", k));
                    query.append("startDate", params.startDate);
                    query.append("endDate", params.endDate);
                    return query.toString();
                },
            });
            setResults(response.data);
        } catch (err) {
            console.error("검색 실패", err);
        } finally {
            setIsLoading(false);
        }
    };

    const sortedResults = [...results].sort((a, b) => {
        const valA = a[sortKey] || "";
        const valB = b[sortKey] || "";
        if (sortKey.includes("Date")) {
            return sortAsc ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
        } else {
            return sortAsc ? valA.localeCompare(valB) : valB.localeCompare(valA);
        }
    });

    useEffect(() => {
        fetchUserKeyword();
        fetchData();
        }, []);

    const handleAddKeyword = () => {

        if ( !newKeyword ) {
            alert("키워드를 입력해주세요");
            return;
        }

        const trimmed = newKeyword.trim();

        if ( keywords.includes(trimmed) ) {
            alert("중복된 키워드 입니다");
            return;
        }

        if ( trimmed.includes(",") ) {
            alert(", 는 사용 불가합니다");
            return;
        }

        if (trimmed) {
            const updated = [...keywords, trimmed];
            setKeywords(updated);
            setHighlighted(trimmed);
            setTimeout(() => setHighlighted(""), 1500);
            setNewKeyword("");
            saveKeywordsToServer(updated); // ← 서버 반영
        }
    };

    const handleRemoveKeyword = (kw) => {
        const updated = keywords.filter((k) => k !== kw);
        setKeywords(updated);
        saveKeywordsToServer(updated);
    };

    const handleResetKeywords = () => {
        setKeywords([]);
        saveKeywordsToServer([]);
    };

    const handleManualSearch = () => { fetchData(); };

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortAsc(!sortAsc);
        } else {
            setSortKey(key);
            setSortAsc(true);
        }
    };


    const renderSortIcon = (key) => {
        if (sortKey !== key) return null;
        return sortAsc ? "▲" : "▼";
    };

    return (
        <div className="slow-animation min-h-screen flex flex-col items-center justify-start px-4 py-10">
            <h1 className="text-4xl font-bold mb-6">통합 검색</h1>

            <div className="w-full max-w-7xl p-8 bg-white rounded-xl flex flex-col gap-6 shadow-sm">

                <div className="w-full flex items-center flex-wrap gap-4">

                    {/*<span className="flex items-center gap-1 text-sm text-red-600">*/}
                    {/*    <BadgeInfoIcon className="w-4 h-4" />*/}
                    {/*    현재 나라장터(API) 서버 문제로 개찰 결과 및 투찰 업체 목록 응답이 반환되고 있지 있으므로 일부 기업에서의 투찰 목록 확인이 어려울 수 있습니다.<br/>*/}
                    {/*    빠른 시일내에 복구할 수 있도록 하겠습니다*/}
                    {/*</span>*/}

                    <SearchBar />

                    날짜 범위 설정 :

                    <input
                        type="date"
                        value={startDate}
                        min={minDate}
                        max={today}
                        onChange={(e) => setStartDate(e.target.value)}
                        disabled={isLoading}
                        className="border px-2 py-1 text-sm rounded"
                    />
                    <input
                        type="date"
                        value={endDate}
                        min={minDate}
                        max={today}
                        onChange={(e) => setEndDate(e.target.value)}
                        disabled={isLoading}
                        className="border px-2 py-1 text-sm rounded"
                    />

                    <button
                        onClick={handleManualSearch}
                        disabled={isLoading}
                        className={`text-white text-sm px-3 py-1 rounded ${isLoading ? 'bg-gray-400' : 'bg-green-500 hover:bg-green-600'}`}
                    >
                        {isLoading ? "로딩 중..." : "조회"}
                    </button>
                </div>

                <div className="flex flex-wrap gap-2 mb-2">
                    {keywords.map((k) => (
                        <span key={k} className={`px-2 py-1 rounded-full text-xs flex items-center border ${highlighted === k ? 'animate-fade-highlight' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
              {k}
                            <button onClick={() => handleRemoveKeyword(k)} className="ml-1 text-red-400 hover:text-red-600 text-xs">✕</button>
            </span>
                    ))}
                </div>

                <div className="flex gap-2 items-center mb-1">
                    <input type="text" placeholder="키워드 추가" value={newKeyword} onChange={(e) => setNewKeyword(e.target.value)} className="border border-gray-300 px-2 py-1 text-sm rounded w-full max-w-xs" />
                    <button onClick={handleAddKeyword} className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-1 rounded">추가</button>
                    <button onClick={handleResetKeywords} className="text-sm px-3 py-1 border border-gray-300 rounded bg-gray-100 hover:bg-gray-200">키워드 초기화</button>
                </div>

                <button onClick={() => setShowNotice(!showNotice)} className="font-bold text-sm text-green-600 hover:underline flex items-center gap-1 self-start">
                    {showNotice ? "Update(v1.0.9) 숨기기" : "Update(v1.0.9) 보기 - 2025-11-21"}
                    {showNotice ? <ChevronUpIcon className="w-4 h-4" /> : <ChevronDownIcon className="w-4 h-4" />}
                </button>

                {showNotice && (
                    <div className="slow-animation text-sm text-gray-700 border border-gray-200 p-4 rounded w-full">
                        <ul className="list-disc list-inside space-y-1">
                            <li>* 나라장터 체계 데이터 구조 변경에 따른 온앤미-인포 시스템 수정중입니다 (이번달 내 처리 완료)</li>
                        </ul>
                    </div>
                )}

                {!isLoading ? (

                    <>
                        <h3>공고 목록 {sortedResults.length}개</h3>

                        <table className="w-full text-sm border-collapse">
                            <thead>
                            <tr className="bg-gray-30 border-b">
                                <th className="px-3 py-2 text-left cursor-pointer" onClick={() => handleSort("title")}>공고명 {renderSortIcon("title")}</th>
                                <th className="px-3 py-2 text-left cursor-pointer" onClick={() => handleSort("postDate")}>공고일자 {renderSortIcon("postDate")}</th>
                                <th className="px-3 py-2 text-left cursor-pointer" onClick={() => handleSort("openDate")}>개찰예정일 {renderSortIcon("openDate")}</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedResults.map((item, index) => (
                                <tr key={index} onClick={() => setSelected(item)} className="slow-animation border-b hover:bg-gray-50 cursor-pointer">
                                    <td className="px-3 py-2">{item.title}</td>
                                    <td className="px-3 py-2">{formatDate(item.postDate)}</td>
                                    <td className="px-3 py-2">{formatDate(item.openDate)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </>

                ) : (
                    <section className="text-gray-500 text-sm bg-white p-4 rounded-xl shadow border flex items-center gap-2">
                        <Loader className="animate-spin w-4 h-4" /> 사업을 가져오고 있습니다
                    </section>
                )}

                {!isLoading && results.length === 0 && (<p className="text-gray-400 text-sm mt-4">검색 결과가 없습니다.</p>)}


            </div>

            <BidDetailModal bidNumber={selected?.bidNoticeNumber} bidNoticeOrder={selected?.bidNoticeOrder} isOpen={!!selected} onClose={() => setSelected(null)} />
        </div>
    );
};

export default MainSearchPage;
